import { __read, __assign } from 'tslib';
import React__default, { useEffect } from 'react';
import LabcorpInvitaeLogoLight from '../../../assets/logos/LabcorpInvitaeLogoLight.esm.js';
import classNames from 'classnames';
import { kebabCase, toLower } from 'lodash';
import { useTextCopy } from '@invitae/cms-blocks';
import { NvMarkdownInline, NvMarkdown } from '@invitae/ids-react';
import { useLocationLiteCookie, useLocationLiteDomain, useCountryLocationPrefix, LocationLiteControl } from '@invitae/nucleobase-engine-contentful';
import CookiebotRenew from '../../CookiebotRenew/CookiebotRenew.esm.js';
import NavLink from '../../NavLink/NavLink.esm.js';
import SocialMediaLinks from '../SocialMediaLinks/SocialMediaLinks.esm.js';
import styles from './GlobalFooter.module.esm.js';

var GlobalFooter = function (_a) {
    var _b;
    var layoutFooterColumn = _a.fields.layoutFooterColumn;
    var className = classNames(styles.root, styles.dark);
    var isWindowDefined = typeof window !== 'undefined';
    var getText = useTextCopy().getText;
    var _c = __read(useLocationLiteCookie(), 2), locationCountry = _c[0], setLocationCountry = _c[1];
    var domain = useLocationLiteDomain();
    var _d = useCountryLocationPrefix(), changeUrlPrefix = _d.changeUrlPrefix, getCountryCodeFromPath = _d.getCountryCodeFromPath, enableCountryLocationControls = _d.enableCountryLocationControls;
    /**
     * Country control
     * if the country cookie changed, the page will refresh with the new country
     */
    useEffect(function () {
        var _a, _b;
        var countryCode = locationCountry.countryCode, setBy = locationCountry.setBy;
        /**
         * If the value was set by user, then we reload the page
         */
        var locationLiteControlChangedCookie = setBy === 'user';
        if (locationLiteControlChangedCookie) {
            setLocationCountry({ countryCode: countryCode, setBy: 'fallback' });
            (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.replace) === null || _b === void 0 ? void 0 : _b.call(_a, "".concat(changeUrlPrefix(window.location.pathname, countryCode)).concat(window.location.search));
        }
    }, [changeUrlPrefix, getCountryCodeFromPath, locationCountry, setLocationCountry, domain]);
    var footerConfig = {
        legalLinks: [
            {
                id: 'cookiePreferences',
                text: getText('cookiePreferences'),
                url: '/cookie',
            },
            {
                id: 'doNotSellMyInfo',
                text: getText('doNotSellMyInfo'),
                url: '/do-not-sell-my-info',
            },
            {
                id: 'privacy',
                text: getText('privacy'),
                url: '/privacy',
            },
            {
                id: 'termsOfUse',
                text: getText('termsOfUse'),
                url: '/terms',
            },
            {
                id: 'accessibility',
                text: getText('accessibility'),
                url: '/accessibility',
            },
            {
                id: 'licensing',
                text: getText('licensing'),
                url: '/licensing',
            },
            {
                id: 'sitemap',
                text: getText('sitemap'),
                url: '/sitemap',
            },
        ],
        text: {
            copyright: getText('copyright'),
            followUs: getText('followUs'),
        },
    };
    var navGroups = layoutFooterColumn.map(function (column, columnIndex) {
        var _a = column.fields, links = _a.links, heading = _a.heading;
        return (React__default.createElement("div", { className: styles.navGroup, key: columnIndex },
            React__default.createElement(NvMarkdownInline, { elementWrapper: "h2" }, heading),
            React__default.createElement("ul", null, links.map(function (link, linkIndex) {
                var _a = link.fields, text = _a.text, linkScheme = _a.linkScheme, analyticsEvents = _a.analyticsEvents;
                var _b = __assign({}, linkScheme.fields), _c = _b.path, path = _c === void 0 ? '' : _c, _d = _b.url, url = _d === void 0 ? '' : _d;
                return (React__default.createElement(NavLink, { analyticsEvents: analyticsEvents, dataCy: "footer_".concat(kebabCase(toLower(text)), "-link"), key: linkIndex, text: text, url: path || url }));
            }))));
    });
    var userLocale = isWindowDefined
        ? (getCountryCodeFromPath((_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.pathname) || 'US').toUpperCase()
        : undefined;
    return (React__default.createElement("footer", { className: className, "data-cy": "footer_root" },
        React__default.createElement("div", { className: classNames('nv-container', styles.footerContainer) },
            React__default.createElement("div", { className: classNames('nv-grid', styles.footerContent) },
                React__default.createElement("div", { className: classNames('nv-fine-print-1', styles.about, styles.gridChild), "data-cy": "footer_logo" },
                    React__default.createElement(LabcorpInvitaeLogoLight, { idSuffix: "footer" }),
                    React__default.createElement("p", null, getText('mission')),
                    React__default.createElement(SocialMediaLinks, { followUsText: footerConfig.text.followUs, isDesktop: true })),
                React__default.createElement("nav", { "aria-label": "Footer links", className: classNames(styles.listOfLinks, styles.gridChild, 'nv-grid') }, navGroups),
                React__default.createElement(SocialMediaLinks, { followUsText: footerConfig.text.followUs }),
                React__default.createElement("div", { className: classNames(styles.subFooter, styles.gridChild), "data-cy": "footer_copyright" },
                    React__default.createElement(NvMarkdown, { className: "nv-fine-print-1", "data-testid": "footer-copyright" }, footerConfig.text.copyright),
                    React__default.createElement("div", { className: styles.footerWrapper },
                        React__default.createElement("ul", { className: styles.legalLinks, "data-cy": "footer_legal_links" }, footerConfig.legalLinks.map(function (_a) {
                            var id = _a.id, text = _a.text, url = _a.url;
                            if (id === 'cookiePreferences') {
                                return React__default.createElement(CookiebotRenew, { key: id, text: text });
                            }
                            else {
                                return React__default.createElement(NavLink, { dataCy: "footer_".concat(kebabCase(toLower(text)), "-link"), key: id, text: text, url: url });
                            }
                        })),
                        enableCountryLocationControls && (React__default.createElement(LocationLiteControl, { displayMode: "countryCode", theme: "dark", userLocale: userLocale }))))))));
};

export { GlobalFooter as default };
//# sourceMappingURL=GlobalFooter.esm.js.map
